import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BookOpen, ArrowLeft, Users, IndianRupee, GraduationCap, UserCheck } from 'lucide-react';
import { canAssignClassTeachers } from '../utils/rolePaths';
import { Label } from './ui/label';
import { classifyFeeType, isFeeCategoryVisible } from '../utils/schoolFeatures';

import { useToast } from '../hooks/use-toast';

const normSubjectId = (id) => {
  const n = Number(id);
  return Number.isNaN(n) || n <= 0 ? null : n;
};

const ClassDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canViewFeature, canManageFeature } = useAuth();
  const canManageSubjects = canManageFeature('subjects');
  const { toast } = useToast();
  const canAssignTeachers = canAssignClassTeachers(user);
  const canViewFees = canViewFeature('fees');
  const [classInfo, setClassInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [feeStructure, setFeeStructure] = useState([]);
  const [timetableData, setTimetableData] = useState({ entries: [], grouped: {} });
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [savingSubjects, setSavingSubjects] = useState(false);

  const fetchData = async () => {
    if (!id) return;
    try {
      const feeParams = { class_id: id };
      if (user?.school_id != null) feeParams.school_id = user.school_id;
      const academicYearId = user?.school_id
        ? localStorage.getItem(`selected_academic_year_id_${user.school_id}`)
        : null;
      const studentParams = {
        class_id: id,
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      const timetableParams = {
        class_id: id,
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      const requests = [
        api.get(`/classes/${id}`),
        api.get('/students', { params: studentParams }),
        api.get('/timetable', { params: timetableParams }),
        api.get('/teachers'),
      ];
      if (canViewFees) {
        requests.splice(2, 0, api.get('/fees/structure', { params: feeParams }));
      }
      const results = await Promise.allSettled(requests);
      let idx = 0;
      const classRes = results[idx++];
      const studentsRes = results[idx++];
      const feeRes = canViewFees ? results[idx++] : { status: 'fulfilled', value: { data: { data: [] } } };
      const timetableRes = results[idx++];
      const teachersRes = results[idx++];
      setClassInfo(classRes.status === 'fulfilled' ? (classRes.value?.data?.data ?? null) : null);
      setStudents(studentsRes.status === 'fulfilled' ? (studentsRes.value?.data?.data ?? []) : []);
      const rawFees = feeRes.status === 'fulfilled' ? (feeRes.value?.data?.data ?? []) : [];
      setFeeStructure(rawFees.filter((row) => {
        const category = row.fee_category || classifyFeeType({
          name: row.fee_type_name,
          code: row.fee_type_code,
        });
        return isFeeCategoryVisible(user, category);
      }));
      setTimetableData(timetableRes.status === 'fulfilled' ? (timetableRes.value?.data?.data ?? { entries: [], grouped: {} }) : { entries: [], grouped: {} });
      setTeachers(teachersRes.status === 'fulfilled' ? (teachersRes.value?.data?.data ?? []) : []);
      if (classRes.status === 'rejected') console.error('Class detail failed', classRes.reason);
      if (studentsRes.status === 'rejected') console.error('Students failed', studentsRes.reason);
      if (feeRes.status === 'rejected') console.error('Fee structure failed', feeRes.reason);
      if (timetableRes.status === 'rejected') console.error('Timetable failed', timetableRes.reason);
      if (teachersRes.status === 'rejected') console.error('Teachers failed', teachersRes.reason);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, user?.school_id, canViewFees]);

  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [id, user?.school_id]);

  useEffect(() => {
    if (!canManageSubjects) return;
    const loadSubjects = async () => {
      try {
        const res = await api.get('/subjects');
        setAvailableSubjects(res.data?.data || []);
      } catch {
        setAvailableSubjects([]);
      }
    };
    loadSubjects();
  }, [canManageSubjects, user?.school_id]);

  useEffect(() => {
    if (!classInfo?.subjects) {
      setSelectedSubjectIds([]);
      return;
    }
    setSelectedSubjectIds(
      classInfo.subjects
        .map((s) => normSubjectId(s.id ?? s.subject_id))
        .filter((sid) => sid != null),
    );
  }, [classInfo?.subjects]);

  const subjectsInClass = useMemo(() => {
    if (classInfo?.subjects?.length) {
      return classInfo.subjects.map((s) => ({
        id: s.id,
        name: s.name,
        teacher: null,
      }));
    }
    const entries = timetableData.entries || [];
    const byId = new Map();
    entries.forEach((e) => {
      if (e.subject_id && !byId.has(e.subject_id)) {
        byId.set(e.subject_id, {
          id: e.subject_id,
          name: e.subject_name,
          teacher: e.teacher_first_name || e.teacher_last_name
            ? `${e.teacher_first_name || ''} ${e.teacher_last_name || ''}`.trim()
            : null,
        });
      }
    });
    return Array.from(byId.values());
  }, [classInfo?.subjects, timetableData.entries]);

  const teachersInClass = useMemo(() => {
    const entries = timetableData.entries || [];
    const byId = new Map();
    entries.forEach((e) => {
      if (e.teacher_id && !byId.has(e.teacher_id)) {
        byId.set(e.teacher_id, {
          id: e.teacher_id,
          name: `${e.teacher_first_name || ''} ${e.teacher_last_name || ''}`.trim(),
        });
      }
    });
    return Array.from(byId.values());
  }, [timetableData.entries]);

  const teacherOptions = useMemo(() => {
    const list = (teachers || []).map((t) => ({
      user_id: t.user_id,
      teacher_table_id: t.id,
      name: `${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email || 'Teacher',
    }));
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [teachers]);

  /** Must match backend: teacher appears on this class in timetable (subject periods). */
  const timetableTeacherTableIds = useMemo(
    () => new Set((teachersInClass || []).map((x) => x.id)),
    [teachersInClass],
  );
  const teachersOnClassTimetable = useMemo(
    () => teacherOptions.filter((t) => timetableTeacherTableIds.has(t.teacher_table_id)),
    [teacherOptions, timetableTeacherTableIds],
  );

  const hasPrimaryClassTeacher = Boolean(
    classInfo?.class_teacher_user_id ||
      (classInfo?.sections || []).some((s) => s.class_teacher_user_id),
  );
  const sections = classInfo?.sections || [];
  const selectedSection = sections.find((s) => s.id === selectedSectionId) ?? null;

  useEffect(() => {
    if (sections.length === 0) {
      setSelectedSectionId(null);
      return;
    }
    if (!selectedSectionId || !sections.some((s) => s.id === selectedSectionId)) {
      setSelectedSectionId(sections[0].id);
    }
  }, [sections, selectedSectionId]);

  const usesSections =
    classInfo?.uses_sections === undefined || classInfo?.uses_sections === null
      ? true
      : Number(classInfo.uses_sections) === 1 || classInfo.uses_sections === true;

  const primaryUserId = usesSections
    ? (selectedSection?.class_teacher_user_id ?? null)
    : (classInfo?.class_teacher_user_id ??
      classInfo?.sections?.find((s) => s.class_teacher_user_id)?.class_teacher_user_id ??
      null);

  const primaryTeacherDisplayName = classInfo
    ? usesSections
      ? selectedSection && (selectedSection.class_teacher_first_name || selectedSection.class_teacher_last_name)
        ? `${selectedSection.class_teacher_first_name || ''} ${selectedSection.class_teacher_last_name || ''}`.trim()
        : ''
      : `${classInfo.class_level_teacher_first_name || ''} ${classInfo.class_level_teacher_last_name || ''}`.trim() ||
        (() => {
          const s = (classInfo.sections || []).find((x) => x.class_teacher_user_id);
          return s ? `${s.class_teacher_first_name || ''} ${s.class_teacher_last_name || ''}`.trim() : '';
        })()
    : '';

  const updateSectionClassTeacher = async (sectionId, teacherUserId) => {
    const value = teacherUserId === 'none' ? null : parseInt(teacherUserId, 10);
    await api.put(`/sections/${sectionId}/class-teacher`, { class_teacher_user_id: value });
    await fetchData();
  };

  const updatePrimaryClassTeacherOnClass = async (teacherUserId) => {
    const value = teacherUserId === 'none' ? null : parseInt(teacherUserId, 10);
    await api.put(`/classes/${id}`, { class_teacher_user_id: value });
    await fetchData();
  };

  const updateSecondaryClassTeacher = async (teacherUserId) => {
    const value = teacherUserId === 'none' ? null : parseInt(teacherUserId, 10);
    await api.put(`/classes/${id}`, { secondary_class_teacher_user_id: value });
    await fetchData();
  };

  const saveAssignedSubjects = async () => {
    setSavingSubjects(true);
    try {
      const subjectIds = selectedSubjectIds
        .map((sid) => Number(sid))
        .filter((sid) => !Number.isNaN(sid) && sid > 0);
      await api.put(`/classes/${id}/subjects`, { subject_ids: subjectIds });
      toast({ title: 'Success', description: 'Assigned subjects updated' });
      await fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update subjects',
        variant: 'destructive',
      });
    } finally {
      setSavingSubjects(false);
    }
  };

  const canEditClassTeachers = canAssignTeachers;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!classInfo) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/classes')} className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Classes
        </Button>
        <p className="text-muted-foreground">Class not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="class-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/classes')} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <BookOpen className="w-7 h-7 text-blue-400" />
              {classInfo.name}
            </h1>
            {classInfo.description && (
              <p className="text-muted-foreground mt-1">{classInfo.description}</p>
            )}
          </div>
        </div>
      </div>

      {/* Sectioned classes: section list + class teacher via first section; non-sectioned: class-level teachers only */}
      {!usesSections && (canEditClassTeachers || hasPrimaryClassTeacher || classInfo.secondary_class_teacher_user_id) && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-muted-foreground" />
              Class teachers
            </CardTitle>
            <p className="text-muted-foreground text-sm font-normal">
              This class does not use sections. Assign teachers here (not Timetable). They must already have a period on this class in Timetable.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {canEditClassTeachers && (
              <div className="rounded-lg border border-border bg-background/40 p-4 space-y-6">
                <div className="space-y-2 max-w-md">
                  <Label className="text-foreground">Class teacher</Label>
                  <Select
                    value={primaryUserId != null && primaryUserId !== '' ? String(primaryUserId) : 'none'}
                    onValueChange={(v) => updatePrimaryClassTeacherOnClass(v).catch(() => {})}
                  >
                    <SelectTrigger className="bg-muted border-border text-foreground">
                      <SelectValue placeholder="Select teacher" />
                    </SelectTrigger>
                    <SelectContent className="border-border">
                      <SelectItem value="none">None</SelectItem>
                      {teachersOnClassTimetable.map((t) => (
                        <SelectItem key={t.user_id} value={String(t.user_id)}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {primaryTeacherDisplayName ? (
                    <p className="text-sm text-muted-foreground">Assigned: {primaryTeacherDisplayName}</p>
                  ) : null}
                  {teachersOnClassTimetable.length === 0 && (
                    <p className="text-sm text-amber-400/90">No teachers on this class&apos;s timetable — add periods in Timetable first.</p>
                  )}
                </div>
                <div className="space-y-2 max-w-md">
                  <Label className="text-foreground">Secondary class teacher</Label>
                  <p className="text-xs text-muted-foreground">Substitute; marks attendance when class teacher absent.</p>
                  <Select
                    disabled={!hasPrimaryClassTeacher}
                    value={
                      classInfo.secondary_class_teacher_user_id
                        ? String(classInfo.secondary_class_teacher_user_id)
                        : 'none'
                    }
                    onValueChange={(v) => updateSecondaryClassTeacher(v).catch(() => {})}
                  >
                    <SelectTrigger className="bg-muted border-border text-foreground disabled:opacity-50">
                      <SelectValue placeholder={hasPrimaryClassTeacher ? 'None' : 'Set class teacher first'} />
                    </SelectTrigger>
                    <SelectContent className="border-border">
                      <SelectItem value="none">None</SelectItem>
                      {teachersOnClassTimetable
                        .filter((t) => String(t.user_id) !== String(primaryUserId ?? ''))
                        .map((t) => (
                          <SelectItem key={t.user_id} value={String(t.user_id)}>{t.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {!hasPrimaryClassTeacher && (
                    <p className="text-xs text-muted-foreground">Set class teacher first to enable secondary.</p>
                  )}
                  {(classInfo.secondary_class_teacher_first_name || classInfo.secondary_class_teacher_last_name) && (
                    <p className="text-sm text-muted-foreground">
                      Assigned:{' '}
                      {`${classInfo.secondary_class_teacher_first_name || ''} ${classInfo.secondary_class_teacher_last_name || ''}`.trim()}
                    </p>
                  )}
                </div>
              </div>
            )}
            {!canEditClassTeachers && (
              <p className="text-sm text-muted-foreground">
                Class teacher: {primaryTeacherDisplayName || '—'}
                {(classInfo.secondary_class_teacher_first_name || classInfo.secondary_class_teacher_last_name) && (
                  <>
                    {' · '}Secondary:{' '}
                    {`${classInfo.secondary_class_teacher_first_name || ''} ${classInfo.secondary_class_teacher_last_name || ''}`.trim()}
                  </>
                )}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {usesSections && (canEditClassTeachers || (classInfo.sections || []).length > 0) && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-muted-foreground" />
              Sections
            </CardTitle>
            {canEditClassTeachers ? (
              <p className="text-muted-foreground text-sm font-normal">
                Assign <strong className="text-foreground font-medium">class teacher</strong> and{' '}
                <strong className="text-foreground font-medium">secondary class teacher</strong> here only (not Timetable).
                School admin or principal only. Teachers must already have a period on this class in Timetable.
              </p>
            ) : (
              <p className="text-muted-foreground text-sm font-normal">Sections for this class</p>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {canEditClassTeachers && (
              <div className="rounded-lg border border-border bg-background/40 p-4 space-y-6">
                {sections.length === 0 ? (
                  <p className="text-sm text-amber-400/90">
                    Add at least one section from the <Link to="/classes" className="text-blue-400 underline">Classes</Link> list (edit class → sections), then return here to assign teachers.
                  </p>
                ) : (
                  <>
                    <div className="space-y-2 max-w-md">
                      <Label className="text-foreground">Section</Label>
                      <p className="text-xs text-muted-foreground">Pick section, then assign class teacher.</p>
                      <Select
                        value={selectedSectionId != null ? String(selectedSectionId) : ''}
                        onValueChange={(v) => setSelectedSectionId(parseInt(v, 10))}
                      >
                        <SelectTrigger className="bg-muted border-border text-foreground">
                          <SelectValue placeholder="Select section" />
                        </SelectTrigger>
                        <SelectContent className="border-border">
                          {sections.map((sec) => (
                            <SelectItem key={sec.id} value={String(sec.id)}>
                              Section {sec.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 max-w-md">
                      <Label className="text-foreground">Class teacher</Label>
                      <p className="text-xs text-muted-foreground">One teacher per section.</p>
                      <Select
                        value={primaryUserId != null && primaryUserId !== '' ? String(primaryUserId) : 'none'}
                        onValueChange={(v) => updateSectionClassTeacher(selectedSectionId, v).catch(() => {})}
                        disabled={!selectedSectionId}
                      >
                        <SelectTrigger className="bg-muted border-border text-foreground">
                          <SelectValue placeholder="Select teacher" />
                        </SelectTrigger>
                        <SelectContent className="border-border">
                          <SelectItem value="none">None</SelectItem>
                          {teachersOnClassTimetable.map((t) => (
                            <SelectItem key={t.user_id} value={String(t.user_id)}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {primaryTeacherDisplayName && selectedSection ? (
                        <p className="text-sm text-muted-foreground">
                          Assigned to Section {selectedSection.name}: {primaryTeacherDisplayName}
                        </p>
                      ) : null}
                      {teachersOnClassTimetable.length === 0 && (
                        <p className="text-sm text-amber-400/90">No teachers on this class&apos;s timetable — add periods in Timetable first.</p>
                      )}
                    </div>

                    <div className="space-y-2 max-w-md">
                      <Label className="text-foreground">Secondary class teacher</Label>
                      <p className="text-xs text-muted-foreground">Substitute; marks attendance when class teacher absent (checkbox on Attendance).</p>
                      <Select
                        disabled={!hasPrimaryClassTeacher}
                        value={
                          classInfo.secondary_class_teacher_user_id
                            ? String(classInfo.secondary_class_teacher_user_id)
                            : 'none'
                        }
                        onValueChange={(v) => updateSecondaryClassTeacher(v).catch(() => {})}
                      >
                        <SelectTrigger className="bg-muted border-border text-foreground disabled:opacity-50">
                          <SelectValue placeholder={hasPrimaryClassTeacher ? 'None' : 'Set class teacher first'} />
                        </SelectTrigger>
                        <SelectContent className="border-border">
                          <SelectItem value="none">None</SelectItem>
                          {teachersOnClassTimetable
                            .filter((t) => String(t.user_id) !== String(primaryUserId ?? ''))
                            .map((t) => (
                              <SelectItem key={t.user_id} value={String(t.user_id)}>{t.name}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {!hasPrimaryClassTeacher && (
                        <p className="text-xs text-muted-foreground">Set class teacher first to enable secondary.</p>
                      )}
                      {(classInfo.secondary_class_teacher_first_name || classInfo.secondary_class_teacher_last_name) && (
                        <p className="text-sm text-muted-foreground">
                          Assigned:{' '}
                          {`${classInfo.secondary_class_teacher_first_name || ''} ${classInfo.secondary_class_teacher_last_name || ''}`.trim()}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {(classInfo.sections || []).length > 0 ? (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Section list</p>
                <div className="flex flex-wrap gap-2">
                  {classInfo.sections.map((sec) => (
                    <div key={sec.id} className="px-4 py-3 bg-muted text-foreground rounded-lg text-sm min-w-[200px]">
                      <div className="font-medium">Section {sec.name}</div>
                      {sec.capacity != null && (
                        <div className="text-muted-foreground text-xs mt-0.5">Capacity: {sec.capacity}</div>
                      )}
                      <div className="mt-2 text-xs text-muted-foreground">
                        Class teacher:{' '}
                        <span className="text-foreground">
                          {sec.class_teacher_first_name || sec.class_teacher_last_name
                            ? `${sec.class_teacher_first_name || ''} ${sec.class_teacher_last_name || ''}`.trim()
                            : '—'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No sections yet.</p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-400" />
              Assigned Subjects
            </CardTitle>
            <p className="text-muted-foreground text-sm font-normal">
              {classInfo?.subjects?.length
                ? 'Subjects assigned to this class'
                : 'Showing subjects from timetable until class subjects are assigned'}
            </p>
          </CardHeader>
          <CardContent>
            {canManageSubjects ? (
              <div className="space-y-4">
                {availableSubjects.length === 0 ? (
                  <p className="text-muted-foreground py-2">No subjects available. Add subjects first.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto rounded-md border border-border p-2">
                    {availableSubjects.map((subject) => (
                      <label key={subject.id} className="flex items-center gap-2 text-sm text-foreground">
                        <input
                          type="checkbox"
                          className="accent-blue-500"
                          checked={selectedSubjectIds.some((sid) => normSubjectId(sid) === normSubjectId(subject.id))}
                          onChange={(e) => {
                            const subjectId = normSubjectId(subject.id);
                            if (!subjectId) return;
                            setSelectedSubjectIds((prev) => {
                              if (e.target.checked) {
                                if (prev.some((sid) => normSubjectId(sid) === subjectId)) return prev;
                                return [...prev, subjectId];
                              }
                              return prev.filter((sid) => normSubjectId(sid) !== subjectId);
                            });
                          }}
                        />
                        <span>{subject.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                <Button
                  type="button"
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={savingSubjects}
                  onClick={saveAssignedSubjects}
                >
                  {savingSubjects ? 'Saving…' : 'Save Assigned Subjects'}
                </Button>
              </div>
            ) : subjectsInClass.length === 0 ? (
              <p className="text-muted-foreground py-4">
                No subjects assigned yet. Open{' '}
                <Link to="/classes" className="text-primary underline">Classes</Link>
                {' '}→ Edit class and select subjects.
              </p>
            ) : (
              <ul className="space-y-2">
                {subjectsInClass.map((s) => (
                  <li key={s.id} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                    <span className="text-foreground font-medium">{s.name}</span>
                    {s.teacher && <span className="text-muted-foreground text-sm">— {s.teacher}</span>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Teachers for this class */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground text-lg flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-400" />
              Teachers ({teachersInClass.length})
            </CardTitle>
            <p className="text-muted-foreground text-sm font-normal">Teachers who teach this class</p>
          </CardHeader>
          <CardContent>
            {teachersInClass.length === 0 ? (
              <p className="text-muted-foreground py-4">No teachers assigned. Add entries in Timetable.</p>
            ) : (
              <ul className="space-y-2">
                {teachersInClass.map((t) => (
                  <li key={t.id}>
                    <Link to={`/hr/teachers/${t.id}`} className="text-blue-400 hover:text-blue-300 font-medium">
                      {t.name || 'Teacher'}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fee structure for this class */}
      {canViewFees && (
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-foreground text-lg flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-amber-500" />
            Fee Structure
          </CardTitle>
          <p className="text-muted-foreground text-sm font-normal">Fees defined for this class</p>
        </CardHeader>
        <CardContent>
          {feeStructure.length === 0 ? (
            <p className="text-muted-foreground py-4">No fee structure set. Add from Fee Management → Fee Structure by Class.</p>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 text-left">
                    <th className="px-4 py-3 text-foreground font-medium">Fee Type</th>
                    <th className="px-4 py-3 text-foreground font-medium">Frequency</th>
                    <th className="px-4 py-3 text-foreground font-medium text-right">Amount (₹)</th>
                    <th className="px-4 py-3 text-foreground font-medium">Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  {feeStructure.map((row) => (
                    <tr key={row.id} className="border-t border-border">
                      <td className="px-4 py-3 text-foreground">{row.fee_type_name}</td>
                      <td className="px-4 py-3 text-foreground">{row.fee_type_frequency || '—'}</td>
                      <td className="px-4 py-3 text-right font-medium text-foreground">₹{Number(row.amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-foreground">{row.due_date ? new Date(row.due_date).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Students in this class */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-foreground text-lg flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-purple-400" />
            Students ({students.length})
          </CardTitle>
          <p className="text-muted-foreground text-sm font-normal">Students assigned to this class</p>
        </CardHeader>
        <CardContent>
          {students.length === 0 ? (
            <p className="text-muted-foreground py-4">No students in this class yet.</p>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 text-left">
                    <th className="px-4 py-3 text-foreground font-medium">Name</th>
                    <th className="px-4 py-3 text-foreground font-medium">Admission No</th>
                    {usesSections && (
                      <th className="px-4 py-3 text-foreground font-medium">Section</th>
                    )}
                    <th className="px-4 py-3 text-foreground font-medium">Roll No</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <Link to={`/students/${s.id}`} className="text-blue-400 hover:text-blue-300 font-medium">
                          {s.first_name} {s.last_name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-foreground">{s.admission_no || '—'}</td>
                      {usesSections && (
                        <td className="px-4 py-3 text-foreground">{s.section_name ? `Section ${s.section_name}` : '—'}</td>
                      )}
                      <td className="px-4 py-3 text-foreground">{s.roll_no ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClassDetail;
